import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Input } from '@newcore/ui/inputs';
import { Display, Text } from '@newcore/ui/typography';
import { SamlService } from '@/api/user-portal-service';
import { isValidTenantSlug } from '@/common/tenant-utils';
import { FeatureFlag } from '@/enums/feature-flag';
import {
  forgetTenant,
  loadRememberedTenants,
  RememberedTenantTiles,
} from '@/features/remembered-tenants';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { logError } from '@/utils/error-logger';

export function TenantSelectionPage() {
  const [slug, setSlug] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberedTenants, setRememberedTenants] = useState(
    loadRememberedTenants
  );
  const isRememberedTenantsEnabled = useFeatureFlag(
    FeatureFlag.REMEMBERED_TENANTS
  );

  const showRememberedTenants =
    isRememberedTenantsEnabled && rememberedTenants.length > 0;
  const subtitle = showRememberedTenants
    ? 'Choose your company to access the user portal'
    : 'Enter your company slug to access the user portal';

  const startSignIn = async (tenant: string) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await SamlService.initSamlAuth(tenant);
      window.location.replace(response.redirect_url);
    } catch (err) {
      logError(err, 'auth:samlInit');
      setError('Failed to initiate SSO login. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedSlug = slug.trim().toLowerCase();

    if (!normalizedSlug) {
      setError('Please enter a company slug');

      return;
    }

    if (!isValidTenantSlug(normalizedSlug)) {
      setError(
        'Invalid slug. Use only lowercase letters, numbers, and hyphens (not at start/end)'
      );

      return;
    }

    await startSignIn(normalizedSlug);
  };

  const handleRemoveTenant = (tenant: string) => {
    setRememberedTenants(forgetTenant(tenant));
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-primary p-4'>
      <div className='text-center max-w-md w-full'>
        <Display
          as='h1'
          size={DisplaySizes.SM}
          weight={FontWeights.BOLD}
          className='text-primary mb-6'
        >
          Welcome to NewCore User Portal
        </Display>
        <Text as='p' size={TextSizes.LG} className='text-tertiary mb-8'>
          {subtitle}
        </Text>

        {showRememberedTenants && (
          <>
            <RememberedTenantTiles
              tenants={rememberedTenants}
              isDisabled={isLoading}
              onSelect={startSignIn}
              onRemove={handleRemoveTenant}
            />
            <Text
              as='p'
              weight={FontWeights.SEMIBOLD}
              className='text-secondary text-left mt-8 mb-3'
            >
              Another company
            </Text>
          </>
        )}

        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <Input
              type='text'
              value={slug}
              onChange={event => {
                setSlug(event.target.value);
                setError(''); // Clear error when user types
              }}
              placeholder='Enter company slug'
              className='h-13.5'
            />
            {error && (
              <Text as='p' className='mt-2 text-error-primary'>
                {error}
              </Text>
            )}
          </div>
          <Button
            type='submit'
            disabled={!slug.trim() || isLoading}
            className='w-full h-auto py-3'
          >
            {isLoading ? 'Redirecting...' : 'Continue'}
          </Button>
        </form>

        <Text as='p' className='text-tertiary mt-6'>
          Example: acme, mycompany, etc.
        </Text>
      </div>
    </div>
  );
}
